/**
 * FeedbackModal — «Reportar un problema» y «Sugerir una mejora» (Envío AP-1).
 * Adjunta sólo contexto técnico (ruta, proyecto, navegador, pantalla); nunca
 * tokens ni datos del navegador que no hagan falta para reproducir.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Lightbulb, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { learningService } from '../../services/learningService';
import { useWorkflowStore } from '../../store/workflowStore';
import useTimelineStore from '../../store/timelineStore';
import { feedbackContext } from './learningModel';
import './learning.css';

const MAX = 4000;
const COPY = {
  problem: {
    title: 'Reportar un problema',
    lead: 'Contanos qué pasó: qué estabas haciendo, qué esperabas y qué viste.',
    placeholder: 'Ej.: al exportar el timeline el progreso llega al 92 % y falla…',
    ok: 'Gracias: recibimos el reporte y lo vamos a revisar.',
  },
  idea: {
    title: 'Sugerir una mejora',
    lead: 'Contanos qué te gustaría que haga la plataforma o qué cambiarías.',
    placeholder: 'Ej.: poder duplicar un nodo con todas sus conexiones…',
    ok: 'Gracias por la idea: la vamos a leer con atención.',
  },
};

export const FeedbackModal = ({ open, initialKind = 'problem', onClose }) => {
  const [kind, setKind] = useState(initialKind);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    setMessage('');
    setTimeout(() => areaRef.current?.focus(), 60);
  }, [open, initialKind]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;
  const copy = COPY[kind] || COPY.problem;
  const text = message.trim();

  const send = async () => {
    if (text.length < 3 || busy) return;
    setBusy(true);
    try {
      const wf = useWorkflowStore.getState();
      await learningService.sendFeedback({
        kind,
        message: text,
        context: feedbackContext({
          projectId: wf.currentProjectId,
          projectName: wf.projectName,
          view: useTimelineStore.getState().isOpen ? 'edicion' : 'canvas',
        }),
      });
      toast.success(copy.ok);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos enviar el mensaje. Probá de nuevo.');
    } finally { setBusy(false); }
  };

  return (
    <div className="sml-learn-overlay" onClick={() => !busy && onClose?.()} data-testid="feedback-modal">
      <section className="sml-learn-modal sml-feedback-modal" role="dialog" aria-modal="true"
               aria-labelledby="sml-feedback-title" onClick={(e) => e.stopPropagation()}>
        <header className="sml-learn-head">
          <div>
            <h2 id="sml-feedback-title">{copy.title}</h2>
            <p>{copy.lead}</p>
          </div>
          <button type="button" className="sml-learn-x" onClick={onClose} aria-label="Cerrar"
                  disabled={busy}><X size={18} /></button>
        </header>
        <div className="sml-feedback-body">
          <div className="sml-feedback-kinds" role="radiogroup" aria-label="Tipo de mensaje">
            <button type="button" role="radio" aria-checked={kind === 'problem'}
                    className={kind === 'problem' ? 'is-on' : ''} onClick={() => setKind('problem')}
                    data-testid="feedback-kind-problem">
              <AlertTriangle size={14} aria-hidden="true" /> Un problema
            </button>
            <button type="button" role="radio" aria-checked={kind === 'idea'}
                    className={kind === 'idea' ? 'is-on' : ''} onClick={() => setKind('idea')}
                    data-testid="feedback-kind-idea">
              <Lightbulb size={14} aria-hidden="true" /> Una mejora
            </button>
          </div>
          <label>
            Mensaje
            <textarea ref={areaRef} value={message} maxLength={MAX}
                      placeholder={copy.placeholder}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(); }}
                      data-testid="feedback-message" />
          </label>
          <span className="sml-feedback-count">{message.length} / {MAX}</span>
          <p className="sml-feedback-hint">
            Adjuntamos el proyecto abierto, la pantalla y el navegador para poder reproducirlo. Nada más.
          </p>
        </div>
        <footer className="sml-learn-foot">
          <span />
          <div className="sml-learn-actions">
            <button type="button" className="sml-learn-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
            <button type="button" className="sml-learn-primary" onClick={send}
                    disabled={busy || text.length < 3} data-testid="feedback-send">
              {busy ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Send size={15} aria-hidden="true" />}
              Enviar
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default FeedbackModal;
